import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import axios from 'axios';

const { FiBookOpen, FiPlus, FiEdit, FiSave, FiX, FiTrash2, FiSearch, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiTag } = FiIcons;

const ChartOfAccounts = () => {
  const [loading, setLoading] = useState(true);
  const [allAccounts, setAllAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    category: '',
    newCategory: ''
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 15,
    totalItems: 0
  });

  useEffect(() => {
    fetchAccounts();
    fetchCategories();
  }, []);

  // Effect for search filtering
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAccounts(allAccounts);
    } else {
      const searchTermLower = searchTerm.toLowerCase();
      const filtered = allAccounts.filter(acc =>
        acc.code.toLowerCase().includes(searchTermLower) ||
        acc.name.toLowerCase().includes(searchTermLower) ||
        (acc.category && acc.category.toLowerCase().includes(searchTermLower))
      );
      setFilteredAccounts(filtered);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }
  }, [searchTerm, allAccounts]);

  // Effect for pagination
  useEffect(() => {
    updatePaginatedData();
  }, [pagination.currentPage, filteredAccounts]);

  const updatePaginatedData = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    const paginatedAccounts = filteredAccounts.slice(startIndex, endIndex);

    setAccounts(paginatedAccounts);

    const totalItems = filteredAccounts.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pagination.itemsPerPage));

    setPagination(prev => ({
      ...prev,
      totalPages,
      totalItems,
      currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/settings/chart-of-accounts');
      setAllAccounts(response.data);
      setFilteredAccounts(response.data);
    } catch (error) {
      console.error('Error fetching chart of accounts:', error);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/settings/chart-of-accounts/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'category') {
      if (value === 'Other') {
        setShowNewCategoryInput(true);
      } else {
        setShowNewCategoryInput(false);
        setFormData(prev => ({ ...prev, newCategory: '' }));
      }
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setEditingAccount(null);
    setFormData({
      code: '',
      name: '',
      category: categories.length > 0 ? categories[0] : '',
      newCategory: ''
    });
    setShowNewCategoryInput(false);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const openEditModal = (account) => {
    setEditingAccount(account);
    const categoryExists = categories.includes(account.category);
    setFormData({
      code: account.code,
      name: account.name,
      category: categoryExists ? account.category : 'Other',
      newCategory: categoryExists ? '' : account.category
    });
    setShowNewCategoryInput(!categoryExists);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.category === 'Other') {
      if (!formData.newCategory.trim()) {
        setError('Please enter a name for the new category.');
        return;
      }
      if (categories.some(c => c.toLowerCase() === formData.newCategory.trim().toLowerCase())) {
        setError(`Category "${formData.newCategory.trim()}" already exists.`);
        return;
      }
    }

    setSaving(true);
    setError('');
    setSuccess('');

    const payload = {
      code: formData.code,
      name: formData.name,
      category: formData.category,
      newCategory: formData.newCategory.trim()
    };

    try {
      if (editingAccount) {
        await axios.put(`/api/settings/chart-of-accounts/${editingAccount.id}`, payload);
        setSuccess('Account updated successfully');
      } else {
        await axios.post('/api/settings/chart-of-accounts', payload);
        setSuccess('Account created successfully');
      }

      fetchAccounts();
      fetchCategories();

      setTimeout(() => {
        setShowModal(false);
      }, 1500);
    } catch (error) {
      console.error('Error saving account:', error);
      setError(error.response?.data?.error || 'Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this account?')) {
      return;
    }

    try {
      await axios.delete(`/api/settings/chart-of-accounts/${id}`);
      fetchAccounts();
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-grey-900">Chart of Accounts</h2>
            <p className="mt-1 text-sm text-grey-600">
              Manage your financial accounts for tracking expenses and revenue.
            </p>
          </div>
          <button
            onClick={openAddModal}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
            Add Account
          </button>
        </div>

        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-grey-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search accounts by code, name, or category..."
                className="w-full pl-10 pr-10 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-grey-400 hover:text-grey-600"
                  onClick={clearSearch}
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              )}
            </div>
            <div className="text-sm text-grey-600">
              {pagination.totalItems} account{pagination.totalItems !== 1 ? 's' : ''} found
            </div>
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden rounded-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-grey-200">
              <thead className="bg-grey-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">Code</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">Category</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-grey-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-grey-200">
                {accounts.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center">
                      <SafeIcon icon={FiBookOpen} className="mx-auto h-12 w-12 text-grey-400" />
                      <h3 className="mt-2 text-sm font-medium text-grey-900">No accounts found</h3>
                      <p className="mt-1 text-sm text-grey-500">
                        {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by adding a new account.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  accounts.map((account) => (
                    <tr key={account.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-grey-900">
                        <div className="flex items-center">
                          <SafeIcon icon={FiTag} className="flex-shrink-0 mr-2 h-5 w-5 text-grey-400" />
                          <span>{account.code}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-grey-500 max-w-xs">{account.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {account.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <button onClick={() => openEditModal(account)} className="text-maroon-600 hover:text-maroon-900" title="Edit">
                            <SafeIcon icon={FiEdit} className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(account.id)} className="text-red-600 hover:text-red-900" title="Delete">
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 bg-grey-50 border-t border-grey-200 flex flex-wrap items-center justify-between">
              <div className="text-sm text-grey-700">
                Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} accounts
              </div>
              <div className="flex space-x-2 mt-2 sm:mt-0">
                <button onClick={() => handlePageChange(1)} disabled={pagination.currentPage === 1} className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed" title="First Page">
                  <SafeIcon icon={FiChevronsLeft} className="w-5 h-5" />
                </button>
                <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1} className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed" title="Previous Page">
                  <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
                </button>
                <div className="flex items-center px-4">
                  <span className="text-grey-700">Page {pagination.currentPage} of {pagination.totalPages}</span>
                </div>
                <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages} className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed" title="Next Page">
                  <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
                </button>
                <button onClick={() => handlePageChange(pagination.totalPages)} disabled={pagination.currentPage === pagination.totalPages} className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed" title="Last Page">
                  <SafeIcon icon={FiChevronsRight} className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-grey-900">{editingAccount ? 'Edit Account' : 'Add Account'}</h3>
                <button onClick={() => setShowModal(false)} className="text-grey-400 hover:text-grey-500">
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex"><div className="flex-shrink-0"><SafeIcon icon={FiIcons.FiAlertTriangle} className="h-5 w-5 text-red-400" /></div><div className="ml-3"><p className="text-sm text-red-700">{error}</p></div></div>
                </div>
              )}
              {success && (
                <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <div className="flex"><div className="flex-shrink-0"><SafeIcon icon={FiIcons.FiCheckCircle} className="h-5 w-5 text-green-400" /></div><div className="ml-3"><p className="text-sm text-green-700">{success}</p></div></div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-grey-700">Account Code</label>
                  <input type="text" id="code" name="code" required className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm" value={formData.code} onChange={handleInputChange} placeholder="e.g. 1001, 5020" />
                </div>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-grey-700">Account Name</label>
                  <input type="text" id="name" name="name" required className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm" value={formData.name} onChange={handleInputChange} placeholder="e.g. Cash on Hand, Office Supplies" />
                </div>
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-grey-700">Account Category</label>
                  <select id="category" name="category" required className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm" value={formData.category} onChange={handleInputChange}>
                    <option value="" disabled>Select a category</option>
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    <option value="Other">Other (add new)</option>
                  </select>
                </div>
                {showNewCategoryInput && (
                   <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ duration: 0.3 }}>
                    <label htmlFor="newCategory" className="block text-sm font-medium text-grey-700">New Category Name</label>
                    <input
                      type="text"
                      id="newCategory"
                      name="newCategory"
                      required
                      className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                      value={formData.newCategory}
                      onChange={handleInputChange}
                      placeholder="e.g. Long-term Asset"
                    />
                  </motion.div>
                )}
                <div className="pt-4 flex justify-end">
                  <button type="button" onClick={() => setShowModal(false)} className="mr-3 px-4 py-2 border border-grey-300 shadow-sm text-sm font-medium rounded-md text-grey-700 bg-white hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500">Cancel</button>
                  <button type="submit" disabled={saving} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    {saving ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Saving...</>) : (<><SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />{editingAccount ? 'Update Account' : 'Create Account'}</>)}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ChartOfAccounts;